import { useDispatch, useSelector, useStore } from 'react-redux';
import { useTranslation } from 'react-i18next'; // Import useTranslation
import { RootState } from '../store';
import {
  setOrientation,
  setLayout,
  setMargin,
  setBackgroundColor,
  setBorderThickness,
  setBorderColor,
  LAYOUT_TEMPLATES,
  Orientation,
  LayoutTemplate,
} from '../store/layoutSlice';
import { Radio, RadioGroup, Field, Input, Label } from '@headlessui/react';
import clsx from 'clsx';
import { useState, useEffect, useCallback } from 'react';
import debounce from 'lodash.debounce';
import throttle from 'lodash.throttle';
import { HiShoppingCart } from 'react-icons/hi';
import { useAddToCart } from '../utils/useAddToCart';

interface LayoutProps {
  editorPreviewRef: React.RefObject<any>;
}

const Layout: React.FC<LayoutProps> = ({ editorPreviewRef }) => {
  const { t } = useTranslation(); // Initialize useTranslation
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const { orientation, selectedLayoutId, margins, backgroundColor, border } = useSelector(
    (state: RootState) => state.layout
  );

  const { handleAddToCart, isAddingToCart, isGeneratingPreview, uploadProgress, cartOverlay } = useAddToCart(editorPreviewRef);

  const [localBgColor, setLocalBgColor] = useState(backgroundColor);
  const [localBorderColor, setLocalBorderColor] = useState(border.color);

  const throttleSetLocalBgColor = useCallback(
    throttle((color: string) => {
      setLocalBgColor(color);
      dispatch(setBackgroundColor(color));
    }, 200), // 200ms throttle
    [dispatch]
  );

  const throttleSetLocalBorderColor = useCallback(
    throttle((color: string) => {
      setLocalBorderColor(color);
      dispatch(setBorderColor(color));
    }, 200), // 200ms throttle
    [dispatch]
  );
  
  // Debounced dispatches for numeric inputs
  const debouncedSetMargin = useCallback(
    debounce((side: keyof RootState['layout']['margins'], value: number) => {
      dispatch(setMargin({ side, value }));
    }, 300),
    [dispatch]
  );

  const debouncedSetBorderThickness = useCallback(
    debounce((value: number) => {
      dispatch(setBorderThickness(value));
    }, 300),
    [dispatch]
  );

  // Handlers for numeric inputs that use debounced dispatch
  const handleMarginChange = (side: keyof RootState['layout']['margins'], value: string) => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue)) {
      // Update local state immediately for responsiveness if needed, or rely on Redux state for display
      debouncedSetMargin(side, numValue);
    }
  };

  const handleBorderThicknessChange = (value: string) => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue)) {
      debouncedSetBorderThickness(numValue);
    }
  };

  useEffect(() => {
    setLocalBgColor(backgroundColor);
  }, [backgroundColor]);

  useEffect(() => {
    setLocalBorderColor(border.color);
  }, [border.color]);

  return (
    <div className="w-full w-[700px] max-w-[700px] mx-auto px-4 space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-white break-words whitespace-normal">{t('pageTitles.layout')}</h1>
        <p className="text-sm text-gray-400 mt-1 break-words whitespace-normal">
          {t('layoutPage.subtitle')}
        </p>
      </div>

      {/* Orientation */}
      <Field>
        <Label className="text-sm font-medium text-white">{t('layoutPage.orientation')}</Label>
        <RadioGroup
          value={orientation}
          onChange={(value: Orientation) => dispatch(setOrientation(value))}
          className="space-y-2 mt-3"
        >
          <div className="grid grid-cols-2 gap-2">
            {(['Portrait', 'Landscape'] as Orientation[]).map((option) => (
              <Radio
                key={option}
                value={option}
                className={({ checked }) =>
                  clsx(
                    'cursor-pointer rounded-lg p-3 text-center text-sm font-medium bg-white/5 text-white',
                    checked && 'ring-2 ring-blue-500 bg-white/10',
                    'focus:outline-none data-[focus]:outline-1 data-[focus]:outline-white'
                  )
                }
              >
                {option === 'Portrait' ? t('layoutPage.portrait') : t('layoutPage.landscape')}
              </Radio>
            ))}
          </div>
        </RadioGroup>
      </Field>

      {/* Layout Templates */}
      <Field>
        <Label className="text-sm font-medium text-white">{t('layoutPage.layoutStyle')}</Label>
        <RadioGroup
          value={selectedLayoutId}
          onChange={(id: string) => dispatch(setLayout(id))}
          className="space-y-2 mt-3"
        >
          <div className="grid grid-cols-3 gap-2">
            {LAYOUT_TEMPLATES.map((template: LayoutTemplate) => {
              return (
                <Radio
                  key={template.id}
                  value={template.id}
                  className={({ checked }) =>
                    clsx(
                      'relative flex cursor-pointer rounded-lg p-1 focus:outline-none aspect-w-3 aspect-h-4',
                      'border',
                      checked
                        ? 'border-blue-500 ring-2 ring-offset-2 ring-offset-gray-900 ring-blue-500 bg-[#111]'
                        : 'border-gray-600 bg-[#222] hover:bg-[#111]'
                    )
                  }
                  aria-label={template.id}
                >
                  <img className="w-full h-full object-cover" src={`/layouts/${template.id}.png`} alt={template.id} />
                </Radio>
              );
            })}
          </div>
        </RadioGroup>
      </Field>

      {/* Marges */}
      <Field>
        <Label className="text-sm font-medium text-white">{t('layoutPage.margins')}</Label>
        <div className="grid grid-cols-4 gap-2 mt-3">
          {(Object.keys(margins) as Array<keyof typeof margins>).map((side) => (
            <div key={side}>
              <Label htmlFor={`margin-${side}`} className="block text-xs font-medium text-gray-400 capitalize">
                {t(`layoutPage.${side}` as any, side) /* Using side as fallback if translation missing */}
              </Label>
              <Input
                id={`margin-${side}`}
                type="number"
                name={`margin-${side}`}
                value={margins[side].toString()} // Ensure value is a string for controlled input
                onChange={(e) => handleMarginChange(side, e.target.value)}
                className="mt-1 block w-full rounded-md bg-white/5 py-1.5 px-3 text-sm/6 text-white focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-white/25 appearance-none"
              />
            </div>
          ))}
        </div>
      </Field>
      
      {/* Background Color */}
      <Field>
        <Label className="text-sm font-medium text-white">{t('layoutPage.backgroundColor')}</Label>
        <div className="mt-3 flex items-center">
          <input 
            type="color" 
            value={localBgColor} 
            onChange={(e) => throttleSetLocalBgColor(e.target.value)} 
            className="w-8 h-8 rounded-md border-gray-600 cursor-pointer focus:ring-blue-500 focus:border-blue-500 bg-transparent mr-2"
          />
          <Input 
            type="text" 
            value={localBgColor} 
            onChange={(e) => throttleSetLocalBgColor(e.target.value)} 
            className="block w-full rounded-md bg-white/5 py-1.5 px-3 text-sm/6 text-white focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-white/25"
            placeholder="#FFFFFF"
          />
        </div>
      </Field>

      {/* Border */}
      <Field>
        <Label className="text-sm font-medium text-white">{t('layoutPage.border')}</Label>
        <div className="grid grid-cols-2 gap-4 mt-3">
          <div>
            <Label htmlFor="border-thickness" className="block text-xs font-medium text-gray-400">
              {t('layoutPage.thickness')}
            </Label>
            <Input
              id="border-thickness"
              type="number"
              name="border-thickness"
              value={border.thickness.toString()} // Ensure value is a string
              onChange={(e) => handleBorderThicknessChange(e.target.value)}
              className="mt-1 block w-full rounded-md bg-white/5 py-1.5 px-3 text-sm/6 text-white focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-white/25 appearance-none"
            />
          </div>
          <div>
            <Label htmlFor="border-color" className="block text-xs font-medium text-gray-400">
              {t('layoutPage.color')}
            </Label>
            <div className="mt-1 flex items-center">
              <input 
                type="color" 
                value={localBorderColor} 
                onChange={(e) => throttleSetLocalBorderColor(e.target.value)} 
                className="w-8 h-8 rounded-md border-gray-600 cursor-pointer focus:ring-blue-500 focus:border-blue-500 bg-transparent mr-2"
              />
              <Input 
                id="border-color"
                type="text" 
                value={localBorderColor} 
                onChange={(e) => throttleSetLocalBorderColor(e.target.value)} 
                className="block w-full rounded-md bg-white/5 py-1.5 px-3 text-sm/6 text-white focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-white/25"
                placeholder="#000000"
              />
            </div>
          </div>
        </div>
      </Field>

      {cartOverlay}
      <button
        onClick={handleAddToCart}
        disabled={isAddingToCart}
        className="w-full mt-6 flex justify-center items-center space-x-2 text-white py-2 rounded-sm text-sm font-medium disabled:opacity-50 disabled:cursor-wait cursor-pointer"
        type="button"
        style={{ backgroundColor: isAddingToCart ? '#FC4C02' : '#FC4C02', opacity: isAddingToCart ? 0.5 : 1 }}
        onMouseOver={e => (e.currentTarget.style.backgroundColor = '#d63a00')}
        onMouseOut={e => (e.currentTarget.style.backgroundColor = '#FC4C02')}
      >
        <HiShoppingCart className="w-4 h-4" />
        <span>{t('common.addToCart', 'Ajouter au panier')}</span>
      </button>
    </div>
  );
};

export default Layout;